import { AuthUser } from '../types';
import { bookmarkService } from './bookmarks';
import { AUTH_CONFIG } from '../config/auth';

if (!AUTH_CONFIG.GOOGLE_CLIENT_ID || AUTH_CONFIG.GOOGLE_CLIENT_ID === 'your-google-client-id-here') {
  console.error(`
    ⚠️  Google OAuth Client ID not configured!
    
    To fix this:
    1. Go to https://console.cloud.google.com/
    2. Create OAuth 2.0 credentials
    3. Add your client ID to .env file:
       REACT_APP_GOOGLE_CLIENT_ID=your-actual-client-id
  `);
}

class AuthService {
  private user: AuthUser | null = null;
  private listeners: ((user: AuthUser | null) => void)[] = [];
  private pendingSignIn: { resolve: (user: AuthUser) => void; reject: (error: any) => void } | null = null;

  constructor() {
    // Check for existing session on load
    this.checkAuthState();
  }

  async signInWithGoogle(): Promise<AuthUser> {
    try {
      // Check if client ID is configured
      if (!AUTH_CONFIG.GOOGLE_CLIENT_ID) {
        throw new Error(AUTH_CONFIG.ERROR_MESSAGES.CLIENT_ID_MISSING);
      }

      // Wait for Google Identity Services to load
      if (!window.google) {
        throw new Error(AUTH_CONFIG.ERROR_MESSAGES.GOOGLE_NOT_LOADED);
      }

      // Initialize Google Identity Services
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: AUTH_CONFIG.GOOGLE_CLIENT_ID,
        scope: 'email profile https://www.googleapis.com/auth/drive.file',
        callback: (response) => {
          if (response.error) {
            console.error('Google OAuth Error:', response.error);
            if (response.error === 'invalid_client') {
              throw new Error(AUTH_CONFIG.ERROR_MESSAGES.INVALID_CLIENT);
            }
            throw new Error(`Google OAuth Error: ${response.error}`);
          }
          
          if (!response.access_token) {
            throw new Error(AUTH_CONFIG.ERROR_MESSAGES.NO_ACCESS_TOKEN);
          }
          
          // Get user info from the token
          this.handleGoogleSignIn(response.access_token);
        },
      });

      // Request access token
      client.requestAccessToken();
      
      // Return a promise that will be resolved in the callback
      return new Promise((resolve, reject) => {
        this.pendingSignIn = { resolve, reject };
      });
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  private async handleGoogleSignIn(accessToken: string) {
    try {
      // Get user info from Google
      const response = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${accessToken}`);
      const userInfo = await response.json();

      // Check if user's email domain is allowed
      const userDomain = userInfo.email.split('@')[1];
      
      if (!AUTH_CONFIG.ALLOWED_EMAIL_DOMAINS.includes(userDomain)) {
        const error = new Error(AUTH_CONFIG.ERROR_MESSAGES.DOMAIN_RESTRICTED(AUTH_CONFIG.ALLOWED_EMAIL_DOMAINS));
        if (this.pendingSignIn) {
          this.pendingSignIn.reject(error);
          this.pendingSignIn = null;
        }
        return;
      }

      const user: AuthUser = {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture
      };

      this.user = user;
      localStorage.setItem('authUser', JSON.stringify(user));
      localStorage.setItem('google_access_token', accessToken);
      this.notifyListeners();
      
      // Sync bookmarks to Google Drive
      try {
        await bookmarkService.syncToGoogleDrive();
      } catch (error) {
        console.error('Error syncing bookmarks:', error);
      }
      
      if (this.pendingSignIn) {
        this.pendingSignIn.resolve(user);
        this.pendingSignIn = null;
      }
      
      // Refresh the page to update all components
      window.location.reload();
    } catch (error) {
      console.error('Error getting user info:', error);
      if (this.pendingSignIn) {
        this.pendingSignIn.reject(error);
        this.pendingSignIn = null;
      }
    }
  }

  async signOut(): Promise<void> {
    try {
      // Revoke access token if available
      const accessToken = localStorage.getItem('google_access_token');
      if (accessToken) {
        await fetch(`https://oauth2.googleapis.com/revoke?token=${accessToken}`);
      }
    } catch (error) {
      console.error('Google sign out error:', error);
    }

    this.user = null;
    localStorage.removeItem('authUser');
    localStorage.removeItem('google_access_token');
    this.notifyListeners();
    
    // Refresh the page to update all components
    window.location.reload();
  }

  // Check if the current access token is still valid
  async isTokenValid(): Promise<boolean> {
    const accessToken = localStorage.getItem('google_access_token');
    if (!accessToken) {
      return false;
    }

    try {
      const response = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${accessToken}`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  // Get a valid access token, refreshing if necessary
  async getValidAccessToken(): Promise<string | null> {
    const isValid = await this.isTokenValid();
    if (isValid) {
      return localStorage.getItem('google_access_token');
    }
    
    // Token is invalid, clear it and return null
    localStorage.removeItem('google_access_token');
    return null;
  }

  getCurrentUser(): AuthUser | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return this.user !== null;
  }

  private checkAuthState(): void {
    const savedUser = localStorage.getItem('authUser');
    if (savedUser) {
      try {
        this.user = JSON.parse(savedUser);
        this.notifyListeners();
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('authUser');
      }
    }
  }

  subscribe(listener: (user: AuthUser | null) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.user));
  }
}

export const authService = new AuthService();
